import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronRight, MapPin, Gauge, Calendar, ChevronDown } from 'lucide-react';
import { Navbar } from '../components/layout/Navbar';
import { SEO } from '../components/SEO';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Parses a slug like:
 *   - "honda-montreal"               → { make: 'honda', city: 'montreal' }
 *   - "honda-civic-laval"            → { make: 'honda-civic', city: 'laval' }
 *   - "suv-honda-brossard"           → { body_type: 'suv', make: 'honda', city: 'brossard' }
 *   - "suv-montreal"                 → { body_type: 'suv', city: 'montreal' }
 * Given the live lists of cities, makes and body types from the backend.
 */
const parseSlug = (slug, { cities, makes, bodyTypes }) => {
  if (!slug) return {};
  const cityMatch = cities.find((c) => slug.endsWith(`-${c.slug}`) || slug === c.slug);
  let remainder = slug;
  let city = null;
  if (cityMatch) {
    city = cityMatch.slug;
    remainder = slug.slice(0, slug.length - cityMatch.slug.length).replace(/-$/, '');
  }
  const bodyMatch = bodyTypes.find((b) => remainder === b.slug || remainder.startsWith(`${b.slug}-`));
  let body_type = null;
  if (bodyMatch) {
    body_type = bodyMatch.slug;
    remainder = remainder.slice(bodyMatch.slug.length).replace(/^-/, '');
  }
  // What's left is make (or make-model). Match longest make slug that prefixes the remainder.
  const makeMatch = [...makes].sort((a, b) => b.slug.length - a.slug.length).find((m) =>
    remainder === m.slug || remainder.startsWith(`${m.slug}-`) || remainder.startsWith(`${m.slug}`)
  );
  const make = makeMatch ? makeMatch.slug : null;
  return { make, body_type, city };
};

const VehicleCard = ({ v }) => {
  const img = v.images?.[0];
  const url = typeof img === 'string' ? img : img?.url;
  const price = v.online_price || v.price;
  const display = url?.includes('cloudinary.com') ? url.replace('/upload/', '/upload/w_500,q_auto,f_auto/') : url;
  return (
    <Link
      to={`/vehicle/${v.id}`}
      className="group bg-white rounded-2xl border border-slate-200 overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all"
      data-testid={`seo-vehicle-${v.id}`}
    >
      <div className="aspect-[4/3] bg-slate-100 overflow-hidden">
        {display ? (
          <img src={display} alt={`${v.year} ${v.make} ${v.model}`} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-slate-400">No image</div>
        )}
      </div>
      <div className="p-4">
        <h3 className="text-base font-bold text-slate-900 group-hover:text-blue-600 transition-colors">
          {v.year} {v.make} {v.model}
        </h3>
        {v.trim && <p className="text-xs text-slate-400 uppercase mb-2">{v.trim}</p>}
        <div className="flex items-center gap-3 text-xs text-slate-500 mt-2">
          {v.mileage != null && (
            <span className="flex items-center gap-1"><Gauge className="h-3 w-3" /> {Number(v.mileage).toLocaleString('fr-CA')} km</span>
          )}
          {v.year && (
            <span className="flex items-center gap-1"><Calendar className="h-3 w-3" /> {v.year}</span>
          )}
        </div>
        {price ? (
          <div className="mt-3 text-blue-600 font-bold">
            {new Intl.NumberFormat('fr-CA', { style: 'currency', currency: 'CAD', minimumFractionDigits: 0 }).format(price)}
          </div>
        ) : null}
      </div>
    </Link>
  );
};

const FaqItem = ({ q, a }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-slate-200 last:border-0">
      <button
        onClick={() => setOpen(!open)}
        className="w-full text-left py-4 flex items-center justify-between gap-4 hover:bg-slate-50 px-4 -mx-4 transition-colors"
      >
        <span className="font-semibold text-slate-900">{q}</span>
        <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <p className="text-sm text-slate-600 leading-relaxed pb-4 pr-8">{a}</p>}
    </div>
  );
};

const SeoLandingPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { slug } = useParams();
  const lang = (i18n.language || 'fr').split('-')[0] === 'fr' ? 'fr' : 'en';
  const [meta, setMeta] = useState(null); // cities/makes/bodyTypes
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  // Load lookup data once
  useEffect(() => {
    Promise.all([
      fetch(`${API}/api/seo/cities`).then((r) => r.json()),
      fetch(`${API}/api/seo/makes`).then((r) => r.json()),
      fetch(`${API}/api/seo/body-types`).then((r) => r.json()),
    ])
      .then(([c, m, b]) => setMeta({ cities: c.cities, makes: m.makes, bodyTypes: b.body_types }))
      .catch(() => setMeta({ cities: [], makes: [], bodyTypes: [] }));
  }, []);

  const parsed = useMemo(() => (meta ? parseSlug(slug, meta) : null), [slug, meta]);

  // Fetch landing data
  useEffect(() => {
    if (!parsed) return;
    if (!parsed.make && !parsed.body_type && !parsed.city) {
      setNotFound(true);
      setLoading(false);
      return;
    }
    setLoading(true);
    const params = new URLSearchParams({ lang });
    if (parsed.make) params.set('make', parsed.make);
    if (parsed.body_type) params.set('body_type', parsed.body_type);
    if (parsed.city) params.set('city', parsed.city);
    fetch(`${API}/api/seo/landing?${params}`)
      .then((r) => r.json())
      .then((d) => setData(d))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [parsed, lang]);

  if (loading || !meta) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
        </div>
      </>
    );
  }

  if (notFound || !data) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex flex-col items-center justify-center gap-4 px-4">
          <p className="text-slate-500 text-lg">{lang === 'en' ? 'Landing page not found' : 'Page non trouvée'}</p>
          <Link to="/inventory" className="text-blue-600 hover:underline">
            {lang === 'en' ? 'Browse inventory' : "Voir l'inventaire"}
          </Link>
        </div>
      </>
    );
  }

  // Build structured data
  const structuredData = [
    {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: data.title,
      description: data.meta_description,
      url: `https://entrepotautomontreal.ca/voitures-usagees/${slug}`,
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: data.faqs.map((f) => ({
        '@type': 'Question',
        name: f.question,
        acceptedAnswer: { '@type': 'Answer', text: f.answer },
      })),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      numberOfItems: data.vehicles.length,
      itemListElement: data.vehicles.slice(0, 30).map((v, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        item: {
          '@type': 'Vehicle',
          name: `${v.year} ${v.make} ${v.model}`,
          url: `https://entrepotautomontreal.ca/vehicle/${v.id}`,
          image: typeof v.images?.[0] === 'string' ? v.images[0] : v.images?.[0]?.url,
        },
      })),
    },
  ];

  // Build internal link grid (related searches) — same city, different makes
  const sameCityOtherMakes = meta.makes
    .filter((m) => m.slug !== parsed.make)
    .slice(0, 12)
    .map((m) => ({
      label: `${m.name} ${data.city_name}`,
      to: `/voitures-usagees/${m.slug}-${data.city}`,
    }));
  const sameMakeOtherCities = meta.cities
    .filter((c) => c.slug !== parsed.city)
    .map((c) => ({
      label: `${data.make || (lang === 'en' ? 'Used cars' : 'Voitures usagées')} ${c.name}`,
      to: parsed.make ? `/voitures-usagees/${parsed.make}-${c.slug}` : `/voitures-usagees/${c.slug}`,
    }));

  return (
    <>
      <SEO
        title={data.title}
        description={data.meta_description}
        url={`https://entrepotautomontreal.ca/voitures-usagees/${slug}`}
        schema={structuredData}
      />
      <Navbar />

      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
        {/* Hero */}
        <div className="bg-gradient-to-br from-[#0c2340] via-[#1a4f85] to-[#0c2340] text-white py-14 md:py-20 px-4">
          <div className="max-w-6xl mx-auto">
            <nav className="text-sm text-blue-200/80 mb-4 flex items-center gap-2 flex-wrap">
              <Link to="/" className="hover:text-white">{lang === 'en' ? 'Home' : 'Accueil'}</Link>
              <ChevronRight className="h-3 w-3" />
              <Link to="/inventory" className="hover:text-white">{lang === 'en' ? 'Inventory' : 'Inventaire'}</Link>
              <ChevronRight className="h-3 w-3" />
              <span className="text-white">{data.h1}</span>
            </nav>
            <h1 className="text-3xl md:text-5xl font-black leading-tight mb-3" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              {data.h1}
            </h1>
            <div className="flex flex-wrap items-center gap-3 text-blue-200/90 text-sm">
              <span className="inline-flex items-center gap-1.5"><MapPin className="h-4 w-4" /> {data.city_name}, QC</span>
              <span className="opacity-50">·</span>
              <span>{data.total} {lang === 'en' ? (data.total === 1 ? 'vehicle' : 'vehicles') : (data.total === 1 ? 'véhicule' : 'véhicules')}</span>
              {data.min_price && data.max_price && data.min_price !== data.max_price && (
                <>
                  <span className="opacity-50">·</span>
                  <span>{new Intl.NumberFormat('fr-CA', { style: 'currency', currency: 'CAD', maximumFractionDigits: 0 }).format(data.min_price)} – {new Intl.NumberFormat('fr-CA', { style: 'currency', currency: 'CAD', maximumFractionDigits: 0 }).format(data.max_price)}</span>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Intro */}
        <div className="max-w-6xl mx-auto px-4 py-10">
          <p className="text-slate-600 leading-relaxed text-base md:text-lg max-w-3xl">
            {data.intro}
          </p>

          {/* Vehicle grid */}
          {data.vehicles.length > 0 ? (
            <>
              <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-6" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                {lang === 'en' ? 'Available vehicles' : 'Véhicules disponibles'}
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                {data.vehicles.map((v) => (
                  <VehicleCard key={v.id} v={v} />
                ))}
              </div>
              {data.total > data.vehicles.length && (
                <div className="mt-8 text-center">
                  <Link
                    to={`/inventory${parsed.make ? `?make=${encodeURIComponent(data.make)}` : ''}`}
                    className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-full font-semibold transition-colors"
                  >
                    {lang === 'en' ? `See all ${data.total} vehicles` : `Voir les ${data.total} véhicules`}
                    <ChevronRight className="h-4 w-4" />
                  </Link>
                </div>
              )}
            </>
          ) : (
            <div className="mt-10 bg-white rounded-2xl border border-slate-200 p-10 text-center">
              <p className="text-slate-600 mb-4">
                {lang === 'en'
                  ? "We don't have a vehicle matching this search right now — but our inventory updates daily."
                  : "Aucun véhicule ne correspond à cette recherche pour l'instant — notre inventaire est mis à jour quotidiennement."}
              </p>
              <Link to="/inventory" className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-2.5 rounded-full font-semibold transition-colors">
                {lang === 'en' ? 'Browse all inventory' : "Voir tout l'inventaire"}
                <ChevronRight className="h-4 w-4" />
              </Link>
            </div>
          )}

          {/* FAQ */}
          <h2 className="text-2xl font-bold text-slate-900 mt-16 mb-2" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            {lang === 'en' ? 'Frequently asked questions' : 'Questions fréquentes'}
          </h2>
          <div className="bg-white rounded-2xl border border-slate-200 px-4 mt-4">
            {data.faqs.map((f, i) => (
              <FaqItem key={i} q={f.question} a={f.answer} />
            ))}
          </div>

          {/* Internal-link cluster — drives crawl + topical authority */}
          <div className="mt-16 grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-3">
                {lang === 'en' ? `Other brands in ${data.city_name}` : `Autres marques à ${data.city_name}`}
              </h3>
              <div className="flex flex-wrap gap-2">
                {sameCityOtherMakes.map((l) => (
                  <Link
                    key={l.to}
                    to={l.to}
                    className="text-xs bg-white border border-slate-200 text-slate-700 hover:border-blue-500 hover:text-blue-600 px-3 py-1.5 rounded-full transition-colors"
                  >
                    {l.label}
                  </Link>
                ))}
              </div>
            </div>
            {parsed.make && (
              <div>
                <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-3">
                  {lang === 'en' ? `${data.make} in other cities` : `${data.make} dans d'autres villes`}
                </h3>
                <div className="flex flex-wrap gap-2">
                  {sameMakeOtherCities.map((l) => (
                    <Link
                      key={l.to}
                      to={l.to}
                      className="text-xs bg-white border border-slate-200 text-slate-700 hover:border-blue-500 hover:text-blue-600 px-3 py-1.5 rounded-full transition-colors"
                    >
                      {l.label}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Final CTA */}
          <div className="mt-16 bg-gradient-to-br from-[#0c2340] to-[#1a4f85] rounded-2xl p-8 text-center text-white">
            <h3 className="text-xl md:text-2xl font-bold mb-2">
              {lang === 'en' ? 'Ready to find your next vehicle?' : 'Prêt à trouver votre prochain véhicule?'}
            </h3>
            <p className="text-blue-200 text-sm mb-6 max-w-md mx-auto">
              {lang === 'en'
                ? 'Get pre-approved in minutes with no impact on your credit score.'
                : 'Obtenez votre pré-approbation en quelques minutes sans impact sur votre cote de crédit.'}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/credit" className="inline-flex items-center justify-center gap-2 bg-white text-[#0c2340] px-6 py-3 rounded-full font-bold hover:bg-blue-50 transition-colors">
                {lang === 'en' ? 'Free credit check' : 'Crédit gratuit'}
              </Link>
              <Link to="/contact" className="inline-flex items-center justify-center gap-2 border border-white/40 hover:bg-white hover:text-[#0c2340] text-white px-6 py-3 rounded-full font-bold transition-colors">
                {lang === 'en' ? 'Contact us' : 'Nous contacter'}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SeoLandingPage;
